import { QuestionMarkCircleIcon } from '@heroicons/react/20/solid';
import {
  Battery100Icon as Battery100IconOutline,
  BoltIcon as BoltIconOutline,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  Battery0Icon,
  Battery100Icon,
  BoltIcon,
} from '@heroicons/react/24/solid';
import { PropsWithChildren } from 'react';
import { parseChargingTime } from '@/lib/chargingTime';
import { ChargingInfoType } from '@/lib/schema/inventory/types';
import { parseRawChargerTypes } from '@/lib/report';
import { TrimSpecs } from '@/lib/schema/build/types';
import Badge, { BadgeVariants } from '@/components/molecules/Badge';
import { VDPChargingCardWrapper } from '@/components/organisms/VDPChargingCardWrapper/VDPChargingCardWrapper';
import { ValueWithUnit } from '@/types';
import {
  Level1ChargerIcon,
  Level2ChargerIcon,
  Level3ChargerIcon,
} from '../../icons';
import { ChargingLevelCard } from './InfoCard';

const WarrantyIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
  >
    <path
      d="M12 17.0001L15 20.0001L20 13.0001M16 3.61914C13.1327 6.33464 9.26084 8.00014 5 8.00014C4.9323 8.00014 4.8647 7.99972 4.79719 7.99888C4.27986 9.57236 4 11.2536 4 13.0002C4 20.4556 9.09909 26.72 16 28.4961C22.9009 26.72 28 20.4556 28 13.0002C28 11.2536 27.7201 9.57236 27.2028 7.99888C27.1353 7.99972 27.0677 8.00014 27 8.00014C22.7392 8.00014 18.8673 6.33464 16 3.61914Z"
      className="stroke-neutral-800"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 16.9998L15 19.9998L20 12.9998"
      className="stroke-brandElectric"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface OverlayWrapperProps {
  title: string;
  onClose?: () => void;
  hideHeader?: boolean;
}

const OverlayWrapper: React.FC<PropsWithChildren<OverlayWrapperProps>> = ({
  children,
  title,
  onClose,
  hideHeader,
}) => {
  return (
    <div className="relative flex flex-auto flex-col overflow-hidden bg-white">
      {/* overlay header */}
      <div className="flex items-center justify-between pt-l">
        {!hideHeader && (
          <div className="text-body1Medium text-neutral-900">{title}</div>
        )}
        {onClose && (
          <div role="button" onClick={onClose}>
            <XMarkIcon className="size-xl stroke-neutral-600" />
          </div>
        )}
      </div>

      {/* overlay content */}
      <div className="flex flex-auto flex-col overflow-y-auto">
        <div className="flex flex-auto py-s">{children}</div>
      </div>
    </div>
  );
};

interface BatteryOverlayProps extends Omit<OverlayWrapperProps, 'title'> {
  batterySize: string;
  fuelEconomy: string | null;
  batteryTypeCard: {
    type: string;
    description: string;
    badge: string;
  } | null;
  horsepower: ValueWithUnit | null;
  ePower: string;
  warrantyString: string;
  warrantyActive: boolean | null;
  hideHeader?: boolean;
}

export const BatteryOverlay: React.FC<BatteryOverlayProps> = ({
  onClose,
  batterySize,
  fuelEconomy,
  batteryTypeCard,
  horsepower,
  ePower,
  warrantyString,
  warrantyActive,
  hideHeader,
}) => {
  return (
    <OverlayWrapper
      title="Battery Guide"
      onClose={onClose}
      hideHeader={hideHeader}
    >
      <div className="grid w-full grid-cols-2 gap-l ml:flex">
        <VDPChargingCardWrapper
          title={'Battery capacity'}
          value={batterySize || 'N/A'}
          subtitle={
            'The amount of energy the EV can hold. Equivalent to miles per gallon for gas vehicles.'
          }
          icon={
            <div className="relative size-[32px]">
              <Battery100Icon className="absolute fill-brandElectric" />
              <Battery100IconOutline className="absolute stroke-neutral-800" />
            </div>
          }
          content={
            fuelEconomy && (
              <div className="mt-l flex">
                <Badge
                  label={`~ ${fuelEconomy}`}
                  variant={BadgeVariants.Gray}
                />
              </div>
            )
          }
        />
        <VDPChargingCardWrapper
          title={'Peak Power'}
          value={ePower}
          subtitle={
            'The power of the EV. Equivalent to horsepower for gas vehicles.'
          }
          icon={
            <div className="relative size-[32px]">
              <BoltIcon className="absolute fill-brandElectric" />
              <BoltIconOutline className="absolute stroke-neutral-800" />
            </div>
          }
          content={
            horsepower && (
              <div className="mt-l flex">
                <Badge
                  label={`~ ${Number(horsepower.value).toLocaleString()} ${horsepower.unit}`}
                  variant={BadgeVariants.Gray}
                />
              </div>
            )
          }
        />

        <VDPChargingCardWrapper
          title={'Warranty'}
          value={warrantyString}
          icon={<WarrantyIcon />}
          subtitle={
            'Most batteries should last at least a decade before needing replacement.'
          }
          content={
            horsepower && (
              <div className="mt-l flex">
                {warrantyActive ? (
                  <Badge label={`Active`} variant={BadgeVariants.Green} />
                ) : (
                  <Badge label={`Expired`} variant={BadgeVariants.Red} />
                )}
              </div>
            )
          }
        />
        {batteryTypeCard && (
          <VDPChargingCardWrapper
            title={'Battery type'}
            value={batteryTypeCard.type}
            subtitle={batteryTypeCard.description}
            icon={
              <div className="relative size-[32px]">
                <Battery0Icon className="absolute fill-neutral-800" />
                <QuestionMarkCircleIcon className="absolute right-0 top-0 size-l fill-brandElectric stroke-white"></QuestionMarkCircleIcon>
              </div>
            }
            content={
              horsepower && (
                <div className="mt-l flex">
                  <Badge
                    label={batteryTypeCard.badge}
                    variant={BadgeVariants.Blue}
                  />
                </div>
              )
            }
          />
        )}
      </div>
    </OverlayWrapper>
  );
};

interface ChargingOverlayProps extends Omit<OverlayWrapperProps, 'title'> {
  chargingInfo: ChargingInfoType;
  trimSpecs: TrimSpecs | null;
  hideHeader?: boolean;
}

export const ChargingOverlay: React.FC<ChargingOverlayProps> = ({
  onClose,
  chargingInfo,
  trimSpecs,
  hideHeader,
}) => {
  const lvl1ChargeTime = parseChargingTime(chargingInfo.level1);
  const lvl2ChargeTime = parseChargingTime(chargingInfo.level2);
  const lvl3ChargeTime = parseChargingTime(chargingInfo.level3);

  const { DCCharger, ACCharger } = parseRawChargerTypes({
    chargingInfo,
    trimSpecs,
  });
  const chargingLevels = [
    {
      level: 'Level 1',
      chargeTime: lvl1ChargeTime
        ? `${lvl1ChargeTime.value} ${lvl1ChargeTime.unit}`
        : 'N/A',
      connectorType: ACCharger.type,
      description:
        'The most basic, but slowest charging option. Level 1 uses a standard household outlet and requires no additional installation.',
      ChargerIcon: Level1ChargerIcon,
    },
    {
      level: 'Level 2',
      chargeTime: lvl2ChargeTime
        ? `${lvl2ChargeTime.value} ${lvl2ChargeTime.unit}`
        : 'N/A',
      connectorType: ACCharger.type,
      description:
        'The best choice for home charging. Level 2 chargers require professional installation by a qualified electrician and allow for smart charging overnight.',
      ChargerIcon: Level2ChargerIcon,
    },
    {
      level: 'Level 3',
      chargeTime: lvl3ChargeTime
        ? `${lvl3ChargeTime.value} ${lvl3ChargeTime.unit}`
        : 'N/A',
      connectorType: DCCharger.type,
      description:
        'The fastest way to charge your EV. Level 3 charging stations are found in public locations such as shopping malls and workplaces.',
      ChargerIcon: Level3ChargerIcon,
    },
  ];

  return (
    <OverlayWrapper
      onClose={onClose}
      title="Charging types guide"
      hideHeader={hideHeader}
    >
      <div className="flex w-full flex-col gap-l iframe-row-collapse:flex-row">
        {chargingLevels.map((level) => (
          <ChargingLevelCard key={level.level} {...level} />
        ))}
      </div>
    </OverlayWrapper>
  );
};
